import { useMemo } from 'react';

import { getNodes } from '@/utils/graphqlUtils';

import { useManageIntegrationDataContext } from '../../../context/manageIntegrationData.context';
import { EntityLinkRow } from '../components/EntityLinkTable.types';
import {
  GetDirectHoldingsToLinkQuery,
  useGetDirectHoldingsToLinkQuery,
} from '../graphql/DirectHoldingsLinkPane.generated';
import {
  GetTrustsBusinessesToLinkQuery,
  useGetTrustsBusinessesToLinkQuery,
} from '../graphql/TrustsBusinessesLinkPane.generated';
import { toEntityLinkRow } from '../linkData.utils';

interface LinkPaneRowsResult {
  rows: EntityLinkRow[];
  loading: boolean;
  /** Re-run the query, e.g. after the user creates an entity from the pane. */
  refetch: () => Promise<unknown>;
}

type LinkQueryData =
  | GetDirectHoldingsToLinkQuery
  | GetTrustsBusinessesToLinkQuery;

/**
 * `node(id:)` resolves to the Node union, so narrow to the Household branch
 * before reading `entities` — both link queries select it only inside
 * `... on Household`.
 */
function toRows(data: LinkQueryData | undefined): EntityLinkRow[] {
  const household = data?.household;
  if (household?.__typename !== 'Household') return [];
  return getNodes(household.entities).map(toEntityLinkRow);
}

export function useDirectHoldingsLinkRows(): LinkPaneRowsResult {
  const { householdId } = useManageIntegrationDataContext();

  const { data, loading, refetch } = useGetDirectHoldingsToLinkQuery({
    variables: { householdId },
    // The pane only mounts once it's open, so this refires each time the user
    // returns — which is what picks up a changed client selection. The rows
    // offer only entities from the household's linked clients, and Apollo
    // wouldn't refetch on its own: the variables are unchanged, only the
    // server-side link they resolve against.
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(() => toRows(data), [data]);
  return { rows, loading, refetch };
}

export function useTrustsBusinessesLinkRows(): LinkPaneRowsResult {
  const { householdId } = useManageIntegrationDataContext();

  const { data, loading, refetch } = useGetTrustsBusinessesToLinkQuery({
    variables: { householdId },
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(() => toRows(data), [data]);
  return { rows, loading, refetch };
}
